import { defineStore } from 'pinia';
import InitialState from '@/InitialState';
import { useProjectContextStore } from '@/Stores/Web/projectContextStore';

import CookiesUtility, { cookieNames } from '@/Utils/CookiesUtility';
import { buildDisplayCurrency } from '@/Utils/CurrencyConversionUtility';

const cookieCurrentDisplayCurrencyId = CookiesUtility.get(cookieNames.displayCurrency);
const currentDisplayCurrencyId = cookieCurrentDisplayCurrencyId ? parseInt(cookieCurrentDisplayCurrencyId) : null;

function getDisplayCurrency(state) {
    if (!state.currentDisplayCurrencyId || useProjectContextStore().isV1) {
        return null;
    }

    // Null also when it is the same currency as the project's - nothing to convert.
    return buildDisplayCurrency(
        state.globalDisplayCurrencies,
        state.currentDisplayCurrencyId,
        state.currentCheckoutCurrencyId);
}

export const useCurrencyStore = defineStore('currency', {
    state: () => ({
        globalDisplayCurrencies: InitialState.globalDisplayCurrencies,
        displayCurrencies: InitialState.displayCurrencies,
        checkoutCurrencies: InitialState.checkoutCurrencies,
        currentCheckoutCurrencyId: InitialState.currentCheckoutCurrencyId,
        currentDisplayCurrencyId,
        isCurrencySelectorVisible: InitialState.currencySelectorVisible,
        availableCurrenciesList: [],
        selectedCurrency: null,
        suggestedCurrency: null,
        selectedOption: 0,
    }),
    actions: {
        setCurrentDisplayCurrencyID(displayCurrencyId) {
            if (displayCurrencyId) {
                const hostname = window.location.hostname;
                // Deliberately omit an expiry date: the display choice lasts for this browser session only.
                CookiesUtility.set(cookieNames.displayCurrency, displayCurrencyId, null, '/', hostname, false);
            }
            else {
                CookiesUtility.remove(cookieNames.displayCurrency, '/', window.location.hostname, false);
            }

            this.currentDisplayCurrencyId = displayCurrencyId;

            return Promise.resolve(true);
        },
        setGlobalDisplayCurrencies(globalDisplayCurrencies) {
            this.globalDisplayCurrencies = globalDisplayCurrencies;
        },
        setCheckoutCurrencies(checkoutCurrencies) {
            this.checkoutCurrencies = checkoutCurrencies;
        },
        setCurrentCheckoutCurrencyID(checkoutCurrencyId) {
            this.currentCheckoutCurrencyId = checkoutCurrencyId;
        },
        setCurrencySelectorVisibility(isVisible) {
            this.isCurrencySelectorVisible = isVisible;
        },
        setAvailableCurrenciesList(data) {
            this.availableCurrenciesList = data;
        },
        setSuggestedCurrency(suggestedCurrency) {
            this.suggestedCurrency = suggestedCurrency;
        },
        setSelectedOption(option) {
            this.selectedOption = option;
        },
        applyCurrencySelection({ currencies, suggested, option, currency }) {
            if (!currencies) {
                return;
            }

            this.setAvailableCurrenciesList(currencies);

            if (suggested) {
                this.setSuggestedCurrency(suggested);
            }

            this.setSelectedOption(option);
            this.setSelectedCurrency(currency);
        },
        // Payment methods are not reloaded here - paymentContextStore does it once per settled state.
        setSelectedCurrency(currency = null) {
            // Always take the incoming object - the rate can change while the currency does not.
            this.selectedCurrency = currency;
        },
    },
    getters: {
        // Converted display currency when one applies, otherwise the checkout currency.
        effectiveCurrency: state => getDisplayCurrency(state)
            || state.checkoutCurrencies.find(c => c.currencyID === state.currentCheckoutCurrencyId) || null,
        currentCheckoutCurrency: state => {
            if (state.currentCheckoutCurrencyId && state.checkoutCurrencies.length > 0) {
                return state.checkoutCurrencies
                    .find(c => c.currencyID === state.currentCheckoutCurrencyId);
            }

            return null;
        },
        // Selected display currency with conversion factors; null for v1 or when it equals the project currency.
        convertedDisplayCurrency: state => getDisplayCurrency(state),
        // Selected display currency as listed globally, without conversion factors.
        selectedDisplayCurrency: state => {
            if (state.currentDisplayCurrencyId) {
                return state.globalDisplayCurrencies.find(c => c.currencyID === state.currentDisplayCurrencyId) || null;
            }

            return null;
        },
        defaultGlobalDisplayCurrency: state => state.globalDisplayCurrencies.find(c => c.isDefault) || null,
        getSystemSelectedCurrency: state => {
            return state.availableCurrenciesList.find( ( { isSelected } ) => isSelected) || null;
        },
        dropdownSelectedCurrency: state => {
            return state.availableCurrenciesList
                .find(c => c.shortName === state.suggestedCurrency) || null;
        },
        conversionRate: state => {
            if (state.selectedCurrency) {
                return state.selectedCurrency.exchangeRateWithCommission;
            }

            return null;
        },
    },
});
